/**
 * Configuration for the "Meninas Mães" scrollytelling map.
 *
 * Replaces the jeo/embedded-storymap block (JEO plugin) with a Mapbox
 * GL JS map + custom scroll, with no dependency on the plugin.
 *
 * IMPORTANT — layer names (must match the Studio style exactly, do NOT
 * translate these — they're literal identifiers, not content):
 *   - 'Tela 1 - Mapa Brasil Taxa Fec'
 *   - 'Tela 2 - Reg. Norte Taxa Fec'
 *   - 'Tela 3 - AL e MA'
 *   - 'Tela 4 - Mun. Norte'
 *   - 'Tela 5 - Munic. MT'
 *   - 'Tela 5 - Centros Munic MT'
 *   - 'Tela 6 - Nova Nazare'
 *   - 'Tela 6 - Nova Nazar'
 *   - 'Tela 7 - Campinapolis'
 *   - 'Tela 7 - Campinap centro'
 *   - 'Tela 8 - Maiores Taxas'
 *   - 'Telas 9-10-11-12 - Capitais preenchidas'
 *   - 'Telas 9-10-11-12 - Capitais centroides'
 *   - 'Hospitais Aborto Legal'
 *
 * `title` and `description` accept HTML (injected via innerHTML in
 * index.js — the content comes from here, not from user input, so
 * that's safe). Use simple tags like <strong> and <a href="...">.
 */

import chapter9Chart from '../../resources/meninas-maes/map/grafico.webp';

const config = {
  // accessToken does NOT live here — it's injected at runtime by PHP
  // (see inc/shortcodes/meninas-maes-storymap.php and wp-config.php),
  // read from AZ_MAPBOX_ACCESS_TOKEN / MAPBOX_ACCESS_TOKEN in .env.
  // index.js resolves this by reading window.azMeninasMaesStorymap.accessToken.

  style: 'mapbox://styles/institutoazmina/cmrt77ti900e601s19ts7gel9',

  showMarkers: false,
  theme: 'light', // 'light' or 'dark'
  alignment: 'left', // 'left', 'center' or 'right'

  chapters: [
    {
      id: 'capitulo-1',
      title: 'Meninas mães por todo o país',
      image: '',
      description: 'Entre 2014 e 2023, em 95% dos municípios do Brasil, pelo menos uma menina entre 10 e 14 anos deu à luz (as áreas mais escuras no mapa mostram os locais com mais casos, e as cruzes verdes indicam os hospitais).',
      location: { center: [-51.11703, -12.96621], zoom: 3.36, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 1 - Mapa Brasil Taxa Fec', opacity: 1 },
        { layer: 'Hospitais Aborto Legal', opacity: 1 },
      ],
      onChapterExit: [
        { layer: 'Tela 1 - Mapa Brasil Taxa Fec', opacity: 0 },
        { layer: 'Hospitais Aborto Legal', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-2',
      title: '',
      image: '',
      description: 'A situação mais grave está na região Norte, com destaque para Roraima, Acre, Amazonas, Amapá e Pará.',
      location: { center: [-65.29786, -5.58571], zoom: 4.17, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 2 - Reg. Norte Taxa Fec', opacity: 0.7 },
      ],
      onChapterExit: [
        { layer: 'Tela 2 - Reg. Norte Taxa Fec', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-3',
      title: '',
      image: '',
      description: 'No Nordeste, Alagoas e Maranhão se destacam entre os estados onde mais meninas de 10 a 14 anos têm filhos.',
      location: { center: [-44.98791, -7.61634], zoom: 4.97, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 3 - AL e MA', opacity: 0.7 },
      ],
      onChapterExit: [
        { layer: 'Tela 3 - AL e MA', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-4',
      title: '',
      image: '',
      description: '7 das 10 cidades com as maiores taxas de fecundidade entre meninas de 10 a 14 anos no período ficam na região Norte. São elas: Assis Brasil (AC), Uiramuta (RR), Itacaja (TO), Jacareacanga (PA), Tocantinia (TO), Goiatins (TO) e Alto Alegre (RR).',
      location: { center: [-65.81544, -4.73317], zoom: 4.12, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 4 - Mun. Norte', opacity: 0.7 },
      ],
      onChapterExit: [
        { layer: 'Tela 4 - Mun. Norte', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-5',
      title: '',
      image: '',
      description: 'As outras três cidades com as maiores taxas estão no Mato Grosso: Campinápolis (34,8 nascidos a cada mil meninas nesta faixa etária), Nova Nazaré (34,5) e General Carneiro (23,6).',
      location: { center: [-53.83042, -13.42743], zoom: 5.87, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 5 - Munic. MT', opacity: 0.7 },
        { layer: 'Tela 5 - Centros Munic MT', opacity: 1 },
      ],
      onChapterExit: [
        { layer: 'Tela 5 - Munic. MT', opacity: 0 },
        { layer: 'Tela 5 - Centros Munic MT', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-6',
      title: '',
      image: '',
      description: 'Em Nova Nazaré (MT), <strong>1 a cada 12</strong> crianças nascidas entre 2014 e 2023 é filha de uma menina com até 14 anos.',
      location: { center: [-51.96316, -14.18617], zoom: 8.87, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 6 - Nova Nazare', opacity: 0.7 },
        { layer: 'Tela 6 - Nova Nazar', opacity: 1 },
      ],
      onChapterExit: [
        { layer: 'Tela 6 - Nova Nazare', opacity: 0 },
        { layer: 'Tela 6 - Nova Nazar', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-7',
      title: '',
      image: '',
      description: 'Campinápolis (MT) também apresentou um cenário alarmante: 287 meninas se tornaram mães — 6,76% dos partos no município entre 2014 e 2023.',
      location: { center: [-53.21728, -14.32923], zoom: 8.24, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 7 - Campinapolis', opacity: 0.7 },
        { layer: 'Tela 7 - Campinap centro', opacity: 1 },
      ],
      onChapterExit: [
        { layer: 'Tela 7 - Campinapolis', opacity: 0 },
        { layer: 'Tela 7 - Campinap centro', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-8',
      title: 'Onde há mais meninas mães, faltam hospitais para aborto legal',
      image: '',
      description: 'Somente 2 das 76 instituições que confirmaram oferecer o procedimento ficam na região Norte: uma em Roraima, outra no Pará (conforme Mapa do Aborto Legal 2022).',
      location: { center: [-61.06266, -11.52720], zoom: 2.95, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Tela 8 - Maiores Taxas', opacity: 1 },
        { layer: 'Hospitais Aborto Legal', opacity: 1 },
      ],
      onChapterExit: [
        { layer: 'Tela 8 - Maiores Taxas', opacity: 0 },
      ],
    },
    {
      id: 'capitulo-9',
      title: 'Capitais concentram hospitais — e maiores números absolutos de meninas mães',
      image: chapter9Chart,
      description: 'Em números absolutos, São Paulo, Rio de Janeiro, Manaus, Fortaleza e Salvador foram as cidades que mais registraram nascimentos de filhos de meninas entre 10 e 14 anos, na última década (2014 a 2023).',
      location: { center: [-55.73762, -11.41812], zoom: 3.81, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Telas 9-10-11-12 - Capitais preenchidas', opacity: 0.7 },
        { layer: 'Telas 9-10-11-12 - Capitais centroides', opacity: 1 },
        { layer: 'Hospitais Aborto Legal', opacity: 1 },
      ],
      onChapterExit: [],
    },
    {
      id: 'capitulo-10',
      title: '',
      image: '',
      description: 'Mesmo com 9 hospitais para aborto legal próximos, a cidade de <strong>São Paulo</strong>, no topo da lista de casos, registrou ao todo 5.264 nascimentos no período de 2014 a 2023.',
      location: { center: [-46.68883, -23.65776], zoom: 8.60, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Telas 9-10-11-12 - Capitais preenchidas', opacity: 0.7 },
        { layer: 'Telas 9-10-11-12 - Capitais centroides', opacity: 1 },
        { layer: 'Hospitais Aborto Legal', opacity: 1 },
      ],
      onChapterExit: [],
    },
    {
      id: 'capitulo-11',
      title: '',
      image: '',
      description: 'Seguido por <strong>Rio de Janeiro</strong>, que registrou 4.710 nascimentos com mães menores de 14 anos na última década.',
      location: { center: [-43.33423, -22.71255], zoom: 8.60, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Telas 9-10-11-12 - Capitais preenchidas', opacity: 0.7 },
        { layer: 'Telas 9-10-11-12 - Capitais centroides', opacity: 1 },
        { layer: 'Hospitais Aborto Legal', opacity: 1 },
      ],
      onChapterExit: [],
    },
    {
      id: 'capitulo-12',
      title: '',
      image: '',
      description: 'E em terceiro lugar está Manaus, com 3.602 nascidos de meninas entre 10 e 14 anos, <strong>sem nenhum hospital que confirmasse fazer aborto legal no estado.</strong> As unidades mais próximas estariam em Roraima e no Pará.',
      location: { center: [-60.27606, -2.99713], zoom: 8.61, pitch: 0, bearing: 0 },
      onChapterEnter: [
        { layer: 'Telas 9-10-11-12 - Capitais preenchidas', opacity: 0.7 },
        { layer: 'Telas 9-10-11-12 - Capitais centroides', opacity: 1 },
        { layer: 'Hospitais Aborto Legal', opacity: 1 },
      ],
      onChapterExit: [
        { layer: 'Telas 9-10-11-12 - Capitais preenchidas', opacity: 0 },
        { layer: 'Telas 9-10-11-12 - Capitais centroides', opacity: 0 },
        { layer: 'Hospitais Aborto Legal', opacity: 0 },
      ],
    },
  ],
};

export default config;
